import { Button, Group, Modal, Select, Stack, Text, TextInput, Textarea } from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { useCreateImportBeat } from 'hooks/importBeats'
import { useAppTranslation } from 'hooks/useTranslation'
import { ImportBeatRow } from 'supabase/importBeats'
import { areaLabels } from './areaLabels'
import { AreaCity, buildAreaOptions, decodeArea } from './beatArea'
import { mapBeatError, refusalMessageKey } from './beatErrors'
import { BeatUrlRefusal, deriveBeat } from './beatUrl'

type AddBeatModalProps = {
  opened: boolean
  onClose: () => void
  cities: AreaCity[]
  /** The loaded beats, to tell a paused duplicate from an active one. */
  beats: ImportBeatRow[]
}

type AddBeatValues = {
  page_url: string
  area: string | null
  notes: string
}

// A bare Facebook home address (https://www.facebook.com/) fails the DB's
// host+path CHECK, so deriveBeat names it not_facebook. To the person pasting
// it, it IS Facebook; what is missing is the Page's own segment.
const FACEBOOK_HOME = /^https?:\/\/[^/]*(facebook|fb)\.com\/?$/i

const refusalKey = (reason: BeatUrlRefusal, input: string) =>
  reason === 'not_facebook' && FACEBOOK_HOME.test(input.trim())
    ? refusalMessageKey('not_single_segment')
    : refusalMessageKey(reason)

/**
 * Add a beat (BEAT-01, D-01, D-14). The handle is derived live from the
 * pasted Page URL with the same rules as the DB CHECKs; a URL that would be
 * refused names its reason before Save. An area (one municipality or one
 * town) is required. A DB refusal still lands on the right field as a
 * translated message, never a raw code.
 */
export const AddBeatModal = ({ opened, onClose, cities, beats }: AddBeatModalProps) => {
  const { t } = useAppTranslation()
  const createImportBeat = useCreateImportBeat()

  const form = useForm<AddBeatValues>({
    initialValues: { page_url: '', area: null, notes: '' }
  })

  const derived = deriveBeat(form.values.page_url)
  const area = decodeArea(form.values.area)
  const existing = derived.ok ? beats.find((beat) => beat.handle === derived.handle) : undefined
  const canSave = derived.ok && area !== null && !existing

  const duplicateMessage = (beat: ImportBeatRow | undefined) =>
    beat && !beat.active ? t('beats.duplicatePaused') : t('beats.error.duplicate')

  const close = () => {
    form.reset()
    onClose()
  }

  const submit = async (values: AddBeatValues) => {
    const decoded = decodeArea(values.area)
    if (!derived.ok || !decoded) return

    const notes = values.notes.trim()
    try {
      await createImportBeat.mutateAsync({
        handle: derived.handle,
        page_url: derived.pageUrl,
        notes: notes === '' ? null : notes,
        ...decoded
      })
      notifications.show({
        title: t('common.success'),
        message: t('beats.saved', { handle: derived.handle }),
        color: 'green'
      })
      close()
    } catch (err) {
      const m = mapBeatError(err)
      const field = m.field === 'area' ? 'area' : m.field === 'page_url' ? 'page_url' : 'general'
      const message =
        m.messageKey === 'beats.error.duplicate' ? duplicateMessage(existing) : t(m.messageKey)
      form.setErrors({ [field]: message })
    }
  }

  const urlHint = () => {
    if (form.values.page_url.trim() === '') return null
    if (!derived.ok) {
      return (
        <Text size="sm" c="red">
          {t(refusalKey(derived.reason, form.values.page_url))}
        </Text>
      )
    }
    if (existing) {
      return (
        <Text size="sm" c="red">
          {duplicateMessage(existing)}
        </Text>
      )
    }
    return (
      <Text size="sm" c="dimmed">
        {t('beats.handlePreview', { handle: derived.handle })}
      </Text>
    )
  }

  return (
    <Modal opened={opened} onClose={close} title={t('beats.add')} size="lg">
      <form onSubmit={form.onSubmit(submit)}>
        <Stack>
          <div>
            <TextInput
              label={t('beats.pageUrlLabel')}
              placeholder={t('beats.pageUrlPlaceholder')}
              required
              data-autofocus
              {...form.getInputProps('page_url')}
            />
            {urlHint()}
          </div>

          <Select
            label={t('beats.areaLabel')}
            placeholder={t('beats.areaPlaceholder')}
            data={buildAreaOptions(cities, areaLabels(t))}
            searchable
            required
            nothingFoundMessage={t('common.nothing_found')}
            {...form.getInputProps('area')}
          />

          <Textarea
            label={t('beats.notesLabel')}
            autosize
            minRows={2}
            {...form.getInputProps('notes')}
          />

          {form.errors.general && (
            <Text size="sm" c="red">
              {form.errors.general}
            </Text>
          )}

          <Group justify="flex-end">
            <Button variant="default" onClick={close}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={!canSave} loading={createImportBeat.isPending}>
              {t('beats.save')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}
